import React, { useState, useEffect } from 'react';
import { Typography, Layout , Row, Col, Input, Button, Tabs, Spin, message, Select, Radio } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { Chart } from 'iconsax-react';
import { GlobalContext } from '../../context/globalContext';
import FornecedoresService from '../../services/dashboard/fornecedores.service';
import InfoCards from '../../components/dashboards/Governanca/fornecedores/InfoCards';
import PlanosAcaoChart from '../../components/dashboards/Governanca/fornecedores/PlanosAcaoChart';
import MaturidadeChart from '../../components/dashboards/Governanca/fornecedores/MaturidadeChart';
import FornecedoresTable from '../../components/dashboards/Governanca/fornecedores/FornecedoresTable';
import FornecedorDetalhes from '../../components/dashboards/Governanca/fornecedores/FornecedorDetalhes';
import ModalEditar from '../Apps/AcaoFornecedores/Fornecedores/components/ModalEditar/ModalEditar';
import PdfButton from '../../components/PdfButton';
import { 
  FornecedoresStats, 
  PlanoAcaoStats, 
  MaturidadeData, 
  Fornecedor,
  FornecedoresFiltros,
  FornecedoresPaginatedResponse
} from '../../types/dashboard/fornecedores.types';

const { Title, Text } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;
const { Option } = Select;

const FornecedoresPage: React.FC = () => {
  const globalContext = React.useContext(GlobalContext);
  const currentCliente = globalContext?.currentCliente;
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<FornecedoresStats | null>(null);
  const [planosAcao, setPlanosAcao] = useState<PlanoAcaoStats | null>(null);
  const [maturidade, setMaturidade] = useState<MaturidadeData | null>(null);
  const [fornecedores, setFornecedores] = useState<FornecedoresPaginatedResponse | null>(null);
  const [todosFornecedores, setTodosFornecedores] = useState<Fornecedor[]>([]);
  const [fornecedoresList, setFornecedoresList] = useState<Fornecedor[]>([]);
  const [loadingFornecedoresList, setLoadingFornecedoresList] = useState<boolean>(false);
  const [showFornecedores, setShowFornecedores] = useState<boolean>(false);
  const [editFornecedor, setEditFornecedor] = useState<any>(null);
  const [filtros, setFiltros] = useState<FornecedoresFiltros>({
    busca: '',
    status: ['Vigente']
  });
  const [selectedFornecedor, setSelectedFornecedor] = useState<string>('');
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    limit: 10
  });
  const [visualizacaoConsolidada, setVisualizacaoConsolidada] = useState<boolean>(true);
  const [visualizandoDetalhes, setVisualizandoDetalhes] = useState<boolean>(false);
  const [fornecedorDetalhado, setFornecedorDetalhado] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState({
    vigentes: 0,
    novos: 0,
    descontinuados: 0
  });

  const fetchAllFornecedores = async () => {
    try {
      if (!currentCliente || !currentCliente._id) {
        return;
      }
      
      const result = await FornecedoresService.getFornecedores({
        page: 1,
        limit: 1000,
        cliente_id: currentCliente._id
      });
      
      let allFornecedores: Fornecedor[] = [];
      
      if (result && result.data && Array.isArray(result.data)) {
        allFornecedores = result.data;
      } else if (result && Array.isArray(result)) {
        allFornecedores = result;
      }
      
      setTodosFornecedores(allFornecedores);
      
      if (allFornecedores.length > 0) {
        const vigentes = allFornecedores.filter(f => f.status === 'Vigente').length;
        const novos = allFornecedores.filter(f => f.status === 'Novo').length;
        const descontinuados = allFornecedores.filter(f => f.status === 'Descontinuado').length;
        
        setStatusCounts({
          vigentes: vigentes || allFornecedores.length,
          novos: novos || 0,
          descontinuados: descontinuados || 0
        });
      }
    } catch (error) {
      // Falha silenciosa
    }
  };

  const loadFornecedoresList = async () => {
    try {
      setLoadingFornecedoresList(true);
      if (!currentCliente || !currentCliente._id) {
        return;
      }

      const params = {
        page: 1,
        limit: 100,
        cliente_id: currentCliente._id
      };

      const result = await FornecedoresService.getFornecedores(params);
      
      if (result.data && Array.isArray(result.data)) {
        setFornecedoresList(result.data);
      } else {
        message.error('Formato de resposta inesperado da API');
      }
    } catch (error) {
      message.error('Erro ao carregar lista de fornecedores');
    } finally {
      setLoadingFornecedoresList(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!currentCliente || !currentCliente._id) {
          setError('Cliente não selecionado ou não disponível');
          setLoading(false);
          return;
        }
        
        const [statsData, planosData, maturidadeData] = await Promise.all([
          FornecedoresService.getStats(currentCliente._id),
          FornecedoresService.getPlanosAcao(undefined, currentCliente._id),
          FornecedoresService.getMaturidade(currentCliente._id)
        ]);
        
        setStats(statsData);
        setPlanosAcao(planosData);
        setMaturidade(maturidadeData);
        
        await fetchAllFornecedores();
        await loadFornecedoresList();
        await fetchFornecedores();
        
        setLoading(false);
      } catch (error) {
        setError('Falha ao carregar dados do dashboard');
        message.error('Erro ao carregar dados do dashboard');
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [currentCliente]); 
  
  const fetchFornecedores = async () => {
    setLoading(true);
    try {
      if (!currentCliente || !currentCliente._id) {
        setError('Cliente não selecionado ou não disponível');
        setLoading(false);
        return;
      }
      
      const statusMap: Record<string, string[]> = {
        'Vigente': ['Vigente', 'vigente', 'ativo', 'Ativo', 'ATIVO', 'VIGENTE'],
        'Novo': ['Novo', 'novo', 'NOVO'],
        'Descontinuado': ['Descontinuado', 'descontinuado', 'DESCONTINUADO', 'inativo', 'Inativo', 'INATIVO']
      };
      
      const allParams = {
        page: 1,
        limit: 1000,
        cliente_id: currentCliente._id,
        search: filtros.busca || undefined
      };
      
      const result = await FornecedoresService.getFornecedores(allParams);
      
      let allFornecedores: Fornecedor[] = [];
      
      if (result && result.data && Array.isArray(result.data)) {
        allFornecedores = result.data;
      } else if (result && Array.isArray(result)) {
        allFornecedores = result;
      }
      
      const statusList = filtros.status;
      let filteredFornecedores = allFornecedores;
      
      if (statusList && statusList.length > 0) {
        const possibleStatuses: string[] = [];
        statusList.forEach(status => {
          if (statusMap[status]) {
            possibleStatuses.push(...statusMap[status]);
          } else {
            possibleStatuses.push(status);
          }
        });
        
        filteredFornecedores = allFornecedores.filter(f => 
          possibleStatuses.includes(f.status || '')
        );
      }
      
      const startIndex = (paginationParams.page - 1) * paginationParams.limit;
      const endIndex = startIndex + paginationParams.limit;
      const paginatedFornecedores = filteredFornecedores.slice(startIndex, endIndex);
      
      setFornecedores({
        data: paginatedFornecedores,
        total: filteredFornecedores.length,
        page: paginationParams.page,
        limit: paginationParams.limit,
        totalPages: Math.ceil(filteredFornecedores.length / paginationParams.limit)
      });
      
      const vigentes = allFornecedores.filter(f => 
        statusMap['Vigente'].includes(f.status || '')
      ).length;
      
      const novos = allFornecedores.filter(f => 
        statusMap['Novo'].includes(f.status || '')
      ).length;
      
      const descontinuados = allFornecedores.filter(f => 
        statusMap['Descontinuado'].includes(f.status || '')
      ).length;
      
      setStatusCounts({
        vigentes,
        novos,
        descontinuados
      });
      
      setError(null);
    } catch (error) {
      setError('Falha ao carregar lista de fornecedores.');
      message.error('Erro ao buscar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentCliente && currentCliente._id) {
      fetchFornecedores();
    }
  }, [filtros, paginationParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentCliente || !currentCliente._id) {
        return;
      }
      
      try {
        setLoading(true);
        
        const planosData = await FornecedoresService.getPlanosAcao(
          selectedFornecedor || undefined, 
          currentCliente._id
        );
        setPlanosAcao(planosData);
        
        const maturidadeData = await FornecedoresService.getMaturidade(
          currentCliente._id,
          selectedFornecedor || undefined
        );
        setMaturidade(maturidadeData);
        
        setError(null);
      } catch (error) {
        setError('Falha ao carregar dados.');
        message.error('Erro ao buscar dados do fornecedor');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFornecedor, currentCliente]);

  const handleStatusChange = (activeKey: string) => {
    let statusList: ('Vigente' | 'Novo' | 'Descontinuado')[] = [];
    
    if (activeKey === 'vigentes') {
      statusList = ['Vigente'];
    } else if (activeKey === 'novos') {
      statusList = ['Novo'];
    } else if (activeKey === 'descontinuados') {
      statusList = ['Descontinuado'];
    }
    
    setFiltros(prev => ({ ...prev, status: statusList }));
    setPaginationParams(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (value: string) => {
    setFiltros(prev => ({ ...prev, busca: value }));
    setPaginationParams(prev => ({ ...prev, page: 1 }));
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPaginationParams({
      page,
      limit: pageSize || 10
    });
  };

  const handleNovoFornecedor = () => {
    setEditFornecedor(null);
    setShowFornecedores(true);
  };

  const showAddFornecedor = () => {
    setShowFornecedores(!showFornecedores);
    if (showFornecedores) {
      setEditFornecedor(null);
    }
  };

  const handleViewFornecedor = (fornecedorId: string) => {
    setFornecedorDetalhado(fornecedorId);
    setVisualizandoDetalhes(true);
    setVisualizacaoConsolidada(false);
  };

  const handleVoltarDetalhes = () => {
    setVisualizandoDetalhes(false);
    setFornecedorDetalhado(null);
    setVisualizacaoConsolidada(true);
  };

  const renderError = () => {
    if (!error) return null;
    
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Title level={4} style={{ color: '#f5222d' }}>{error}</Title>
        <Button type="primary" onClick={() => window.location.reload()}>
          Tentar novamente
        </Button>
      </div>
    );
  };
  
  const totalFornecedores = fornecedores?.total || 
                           (fornecedores?.data ? fornecedores.data.length : 0);

  return (
    <Content style={{ padding: '0 24px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          <Chart size={22} style={{ marginRight: "8px" }} /> Dashboard Fornecedores
        </Title>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end'
        }}>
          <Radio.Group 
            value={visualizacaoConsolidada ? 'consolidada' : 'detalhada'}
            onChange={(e) => {
              const isConsolidada = e.target.value === 'consolidada';
              setVisualizacaoConsolidada(isConsolidada);
              
              if (!isConsolidada) {
                if (fornecedores && fornecedores.data && fornecedores.data.length > 0) {
                  setFornecedorDetalhado(fornecedores.data[0].id);
                  setVisualizandoDetalhes(true);
                } else {
                  message.info('Não há fornecedores disponíveis para visualizar');
                }
              } else {
                setVisualizandoDetalhes(false);
                setFornecedorDetalhado(null);
              }
            }}
            buttonStyle="solid"
            style={{ marginBottom: 8 }}
          >
            <Radio.Button 
              value="consolidada" 
              style={{ width: '240px', textAlign: 'center' }}
            >
              Visualização Consolidada
            </Radio.Button>
            <Radio.Button 
              value="detalhada" 
              style={{ width: '240px', textAlign: 'center' }}
            >
              Informações Gerais
            </Radio.Button>
          </Radio.Group>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Selecione o tipo de visualização desejada
          </Text>
        </div>
      </div>

      {visualizacaoConsolidada && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
          <Text style={{ marginRight: 8, fontSize: 16 }}>Selecionar Fornecedor: </Text>
          <Select
            placeholder="Todos os fornecedores"
            style={{ width: 300 }}
            value={selectedFornecedor || undefined}
            onChange={(value) => setSelectedFornecedor(value)}
            allowClear
            loading={loadingFornecedoresList}
            disabled={loadingFornecedoresList}
            notFoundContent={fornecedoresList.length === 0 ? "Nenhum fornecedor encontrado" : null}
            showSearch
            optionFilterProp="children"
          >
            {fornecedoresList.map(fornecedor => (
              <Option key={fornecedor.id} value={fornecedor.id}>
                {fornecedor.razaoSocial || "Fornecedor sem nome"}
              </Option>
            ))}
          </Select>
          {loadingFornecedoresList && <Spin size="small" style={{ marginLeft: 8 }} />}
        </div>
      )}

      {loading && !stats ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        renderError()
      ) : (
        <>
          {visualizandoDetalhes && fornecedorDetalhado ? (
            <FornecedorDetalhes 
              fornecedorId={fornecedorDetalhado} 
              onClose={handleVoltarDetalhes}
            />
          ) : (
            <>
              {stats && currentCliente && <InfoCards stats={stats} clienteId={currentCliente._id} />}
              
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                {loading || !planosAcao ? (
                  <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin size="large" />
                  </div>
                ) : planosAcao ? (
                  <PlanosAcaoChart data={planosAcao} />
                ) : null}
                </Col>
                <Col xs={24} lg={12}>
                  {loading && !maturidade ? (
                    <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <MaturidadeChart data={maturidade} loading={loading} />
                  )}
                </Col>
              </Row>
              
              <div style={{ marginTop: 24 }}>
                <Tabs defaultActiveKey="vigentes" onChange={handleStatusChange}>
                  <TabPane tab={`Vigentes (${statusCounts.vigentes || 0})`} key="vigentes" />
                  <TabPane tab={`Novos (${statusCounts.novos || 0})`} key="novos" />
                  <TabPane tab={`Descontinuados (${statusCounts.descontinuados || 0})`} key="descontinuados" />
                </Tabs>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Input
                    placeholder="Buscar fornecedores"
                    prefix={<SearchOutlined />}
                    style={{ width: 300 }}
                    value={filtros.busca}
                    onChange={e => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
                    onPressEnter={e => handleSearch((e.target as HTMLInputElement).value)}
                  />
                  
                  <div>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      style={{ marginRight: 8 }}
                      onClick={handleNovoFornecedor}
                    >
                      Adicionar Fornecedor
                    </Button>
                    <PdfButton 
                            clienteId={currentCliente?._id || ''}
                            tipo="dashboard"
                            filtros={{
                              search: filtros.busca,
                              status: filtros.status
                            }}
                            texto="Gerar Relatórios" fornecedorId={undefined}
                            fornecedorSelecionado={selectedFornecedor}
                            />
                  </div>
                </div>

                <ModalEditar 
                  editFornecedor={editFornecedor}
                  showFornecedores={showFornecedores}
                  showAddFornecedor={showAddFornecedor}
                  clientId={currentCliente?._id}
                />
                
                {fornecedores && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <Text>
                        Exibindo {fornecedores.data?.length || 0}
                        {totalFornecedores > 0 ? ` de ${totalFornecedores}` : ''} 
                        {' '}fornecedores
                      </Text>
                    </div>
                    <FornecedoresTable
                      data={fornecedores.data || []}
                      total={fornecedores.total || 0}
                      current={paginationParams.page}
                      pageSize={paginationParams.limit}
                      onPaginationChange={handlePaginationChange}
                      loading={loading}
                      onViewFornecedor={handleViewFornecedor}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Content>
  );
};

export default FornecedoresPage;